import { useMutation } from '@apollo/client/react';
import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { Skeleton } from '@optools/components/ui/skeleton';
import { ASSIGN_TARGETS } from '@optools/services/operator-gateway/mutations';
import { GET_CHANNEL, GET_TARGET } from '@optools/services/operator-gateway/queries';
import {
  TargetChangeInput,
  TargetView,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { difference } from 'es-toolkit';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { PatchedChannel, usePartnerData } from '../hooks/usePartnerData';
import { createTargetId } from '../utils/targets';

export interface MappingConfirmationDialogProps {
  currentChannel: PatchedChannel | null | undefined;
  currentChannelTargetIds: Set<string>;
  currentTarget: TargetView | null | undefined;
  currentTargetChannelId: number | null;
  disabled: boolean;
  loading: boolean;
  selectedChannelId: number | null;
  selectedTargetIds: Set<string>;
}

export function MappingConfirmationDialog({
  currentChannel,
  currentChannelTargetIds,
  currentTarget,
  currentTargetChannelId,
  disabled,
  loading,
  selectedChannelId,
  selectedTargetIds,
}: MappingConfirmationDialogProps) {
  const [open, setOpen] = useState(false);
  const { channelMap, targetMap } = usePartnerData();
  const mappingTitle = currentChannel?.name || currentTarget?.name;

  const currentTargetMapping = (() => {
    if (currentTargetChannelId) {
      const channel = channelMap.get(currentTargetChannelId);
      if (channel) return <li>{channel.name}</li>;
    }
  })();

  const currentChannelMapping = [...currentChannelTargetIds.values()].map((targetId) => {
    const target = targetMap.get(targetId);
    return target && <li key={targetId}>{target.name}</li>;
  });

  const updatedTargetMapping = (() => {
    if (selectedChannelId) {
      const channel = channelMap.get(selectedChannelId);
      if (channel) return <li> {channel.name}</li>;
    }
  })();

  const updatedChannelMapping = [...selectedTargetIds.values()].map((targetId) => {
    const target = targetMap.get(targetId);
    return target && <li key={targetId}>{target.name}</li>;
  });

  const [assignTargetMutation, assignTargetResult] = useMutation(ASSIGN_TARGETS, {
    onError: () => setOpen(false),
  });

  const handleUpdateChannelMapping = async () => {
    if (!currentChannel) return;

    const targetsToRemove = difference([...currentChannelTargetIds], [...selectedTargetIds]).reduce<
      TargetChangeInput[]
    >((acc, targetId) => {
      const target = targetMap.get(targetId);
      if (target) {
        acc.push({ channelId: null, target: { id: target.id, type: target.type } });
      }
      return acc;
    }, []);

    const targetsToAdd = difference([...selectedTargetIds], [...currentChannelTargetIds]).reduce<
      TargetChangeInput[]
    >((acc, targetId) => {
      const target = targetMap.get(targetId);
      if (target) {
        acc.push({ channelId: currentChannel.id, target: { id: target.id, type: target.type } });
      }
      return acc;
    }, []);

    const targetChanges = [...targetsToRemove, ...targetsToAdd];

    await assignTargetMutation({
      awaitRefetchQueries: true,
      onCompleted: ({ assignTargets }) => {
        const failedTargets = assignTargets
          .filter(({ success }) => !success)
          .map((response) => {
            const id = createTargetId(response.target);
            const target = targetMap.get(id);
            return target?.name;
          });

        if (failedTargets.length) {
          toast.error('Target Channel Mapping Update Failed', {
            description:
              'Failed to assign channel to the following targets: ' + failedTargets.join(', '),
            duration: Infinity,
          });
        } else {
          toast.success('Target Channel Mapping Updated', {
            description: 'The channel mapping was changed successfully.',
          });
        }

        setOpen(false);
      },
      refetchQueries: [{ query: GET_CHANNEL, variables: { name: currentChannel.name } }],
      variables: { targetChanges },
    });
  };

  const handleUpdateTargetMapping = async () => {
    if (!currentTarget) return;

    const { id, type } = currentTarget;

    await assignTargetMutation({
      awaitRefetchQueries: true,
      onCompleted: ({ assignTargets }) => {
        const { reason, success } = assignTargets[0];
        if (!success) {
          toast.error('Target Channel Mapping Update Failed', {
            description: reason,
            duration: Infinity,
          });
        } else {
          toast.success('Target Channel Mapping Updated', {
            description: 'The target mapping was changed successfully.',
          });
        }

        setOpen(false);
      },
      refetchQueries: [{ query: GET_TARGET, variables: { name: currentTarget.name } }],
      variables: {
        targetChanges: [{ channelId: selectedChannelId, target: { id, type } }],
      },
    });
  };

  const handleConfirm = async () => {
    if (currentChannel) handleUpdateChannelMapping();
    if (currentTarget) handleUpdateTargetMapping();
  };

  const handleClose = (nextOpen: boolean) => {
    if (assignTargetResult.loading) return;
    setOpen(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button className="w-36" disabled={disabled} loading={assignTargetResult.loading}>
          Confirm
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-max">
        <DialogTitle className="font-normal" data-testid="mapping-dialog-title">
          Confirm Mapping For: <span className="font-bold">{mappingTitle}</span>
        </DialogTitle>
        <DialogDescription className="sr-only">
          Validate that the updated mappings are correct before submitting.
        </DialogDescription>

        <section className="mt-4 grid w-full grid-cols-[1fr_auto_1fr] gap-2 overflow-auto">
          <div className="flex flex-col gap-2">
            <h2 id="current-mapping-label">Current</h2>
            <Card
              aria-labelledby="current-mapping-label"
              className="max-h-80 min-h-72 min-w-72 flex-1 overflow-y-auto p-4">
              {loading ? (
                <MappingLoadingSkeleton />
              ) : (
                <ul className="space-y-0.5">
                  {currentChannel && currentChannelMapping}
                  {currentTarget && currentTargetMapping}
                </ul>
              )}
            </Card>
          </div>

          <ArrowRight className="self-center text-xl" />

          <div className="flex flex-col gap-2">
            <h2 className="text-right" id="updated-mapping-label">
              Updated
            </h2>
            <Card
              aria-labelledby="updated-mapping-label"
              className="max-h-80 min-h-72 min-w-72 flex-1 overflow-y-auto p-4">
              {loading ? (
                <MappingLoadingSkeleton />
              ) : (
                <ul className="space-y-0.5">
                  {currentChannel && updatedChannelMapping}
                  {currentTarget && updatedTargetMapping}
                </ul>
              )}
            </Card>
          </div>
        </section>

        <DialogFooter className="sm:justify-center sm:[&>button]:w-32">
          <DialogClose asChild>
            <Button disabled={assignTargetResult.loading} variant="secondary">
              Cancel
            </Button>
          </DialogClose>
          <Button
            disabled={assignTargetResult.loading || loading}
            loading={assignTargetResult.loading}
            onClick={handleConfirm}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MappingLoadingSkeleton = () => {
  return (
    <div className="space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
    </div>
  );
};
